import Intercom, { boot, onHide, onShow, shutdown } from "@intercom/messenger-js-sdk";
import { toast } from "sonner";
import type { UserResponse } from "@brev-io/common/src/types/users";
import { createLogger } from "@brev-io/common/src/utils/logger";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "./support-email";

const logger = createLogger("intercom");

export { SUPPORT_EMAIL, SUPPORT_MAILTO };

/** How long `openIntercom` waits for the messenger's `onShow` before offering email instead. */
export const SHOW_CONFIRM_TIMEOUT_MS = 3000;

export type IntercomUser = {
    user_id: string;
    email: string;
    name: string;
    custom_attributes: {
        last_active: string;
        job_role?: string;
        job_title?: string;
        permissions?: string[];
    };
};

/** A Brev user's own profile: who the messenger is told it is talking to. */
export type IntercomPerson = Pick<UserResponse, "userId" | "email" | "firstName" | "lastName"> &
    Partial<Pick<UserResponse, "jobRole" | "jobTitle" | "permissions">>;

/**
 * BRE-5377 / BRE-5736: Intercom lives in the bottom-LEFT gutter. The
 * bottom-right is the Operator chat panel's (`AgentChatBubble`) and the bottom
 * centre the Operator bar's; `alignment` anchors the open panel and the
 * new-message pop-ups alike, so "right" covers the chat. The launcher stays
 * hidden: the messenger opens only from Help & Support (`openIntercom()`).
 * Shared by every boot path (anonymous, identified, the reboot on a change of
 * person) so they can't drift; an `update` sends user attributes only.
 */
const INTERCOM_PLACEMENT = {
    horizontal_padding: 10,
    vertical_padding: 60,
    alignment: "left",
    hide_default_launcher: true
} as const;

export const initializeIntercom = (
    appId: string,
    user?: {
        userId: string;
        email: string;
        firstName: string;
        lastName: string;
        createdAt?: number;
    }
) => {
    if (typeof window === "undefined") return;

    if (!appId) {
        console.error("Intercom App ID is not defined");
        return;
    }

    const settings = user
        ? {
              app_id: appId,
              user_id: user.userId,
              name: `${user.firstName} ${user.lastName}`.trim(),
              email: user.email,
              created_at: user.createdAt || Math.floor(Date.now() / 1000),
              ...INTERCOM_PLACEMENT
          }
        : { app_id: appId, ...INTERCOM_PLACEMENT };

    Intercom(settings);
    bootedAppId = appId;
    messengerState = "booted";
    identifiedUserId = user?.userId ?? null;
    registerOpenSignal();
};

function registerOpenSignal() {
    // Re-registered on every boot (StrictMode remounts IntercomWrapper after a
    // shutdown); the widget keeps every registration, so the handlers are idempotent.
    onShow(handleMessengerShown);
    onHide(handleMessengerHidden);
}

// BRE-9831: Intercom keeps a session's first person through an `update` that
// names another user_id, so a change of person needs shutdown then boot. Only
// a shutdown for an identity change is rebooted here: one made elsewhere (an
// auth page whose URL carries tokens, logout) stays down until its owner boots.
let bootedAppId: string | null = null;
let messengerState: "none" | "booted" | "shut-down" | "shut-down-for-identity" = "none";
let identifiedUserId: string | null = null;
let identitySuspended = false;
let lastPerson: IntercomPerson | null = null;

export function intercomIdentity(person: IntercomPerson): IntercomUser {
    return {
        user_id: person.userId,
        email: person.email,
        name: person.firstName && person.lastName ? `${person.firstName} ${person.lastName}` : person.email,
        custom_attributes: {
            last_active: new Date().toISOString(),
            job_role: person.jobRole,
            job_title: person.jobTitle,
            permissions: person.permissions,
        },
    };
}

/**
 * BRE-9831: who the messenger talks to. `sessionUserId` is the user the server
 * rendered this page for: a refetched `user` that differs (impersonation began
 * in another tab) is not this page's person. While impersonating, `user` is
 * the impersonated account, so it is the real person, or nobody when their
 * profile did not load; never the account being impersonated.
 */
export function messengerPerson(
    user: IntercomPerson | null,
    sessionUserId: string,
    impersonatorUserId: string | null | undefined,
    impersonator: IntercomPerson | null | undefined
): IntercomPerson | null {
    if (!user || user.userId !== sessionUserId) return null;
    if (!impersonatorUserId) return user;
    return impersonator?.userId === impersonatorUserId ? impersonator : null;
}

/** Tells the messenger who it is talking to, rebooting it when that person changes. */
export const identifyIntercom = (person: IntercomPerson): void => {
    if (typeof window === "undefined" || !window.Intercom || identitySuspended) return;
    lastPerson = person;
    if (messengerState === "shut-down") return;
    const identity = intercomIdentity(person);
    const personChanged = identifiedUserId !== null && identifiedUserId !== identity.user_id;
    if ((personChanged || messengerState === "shut-down-for-identity") && bootedAppId) {
        if (messengerState === "booted") shutdownIntercom();
        boot({ app_id: bootedAppId, ...INTERCOM_PLACEMENT, ...identity });
        messengerState = "booted";
        registerOpenSignal();
    } else {
        window.Intercom("update", identity);
    }
    identifiedUserId = identity.user_id;
};

/**
 * BRE-9831: ends the session before a reload into another identity (Switch
 * user, View as member), and ignores identities until the page is replaced, so
 * the account being switched to is never told to the messenger first.
 */
export const shutdownIntercomForIdentityChange = () => {
    identitySuspended = true;
    // A messenger already down (an auth page) stays its owner's to boot.
    const wasBooted = messengerState === "booted";
    shutdownIntercom();
    if (wasBooted) messengerState = "shut-down-for-identity";
};

/**
 * Undoes `shutdownIntercomForIdentityChange` when the switch failed and the
 * page stays: boots the messenger again as the person it last had, since
 * nothing else re-identifies an unchanged user.
 */
export const resumeIntercomIdentity = () => {
    identitySuspended = false;
    if (lastPerson) identifyIntercom(lastPerson);
};

// BRE-7685: "show" is the only way to learn the messenger works, because a
// messenger whose app_id Intercom rejects ("App Not Found") still installs a
// real `window.Intercom` and reports `booted: true`; it just never renders.
// So `openIntercom` treats `onShow` as the success signal and falls back to
// email when it does not arrive.
let messengerOpen = false;
let pendingShowTimer: ReturnType<typeof setTimeout> | null = null;
let fallbackToastId: string | number | null = null;

function clearPendingShow() {
    if (pendingShowTimer) clearTimeout(pendingShowTimer);
    pendingShowTimer = null;
}

function handleMessengerShown() {
    messengerOpen = true;
    clearPendingShow();
    // A slow first load can open the messenger after the fallback appeared.
    if (fallbackToastId !== null) toast.dismiss(fallbackToastId);
    fallbackToastId = null;
}

function handleMessengerHidden() {
    messengerOpen = false;
}

function offerEmailInstead(reason: string) {
    logger.error(`Chat with support is unavailable: ${reason}`);
    if (fallbackToastId !== null) toast.dismiss(fallbackToastId);
    fallbackToastId = toast.error("Chat isn't available right now", {
        description: `Email ${SUPPORT_EMAIL} and the Brev team will get back to you.`,
        action: {
            label: "Email support",
            onClick: () => {
                window.location.href = SUPPORT_MAILTO;
            }
        }
    });
}

/** Ends the Intercom session (logout), so the next person on this browser does not inherit the conversation. */
export const shutdownIntercom = () => {
    if (typeof window === "undefined" || !window.Intercom) return;
    shutdown();
    messengerOpen = false;
    clearPendingShow();
    messengerState = "shut-down";
    identifiedUserId = null;
};

export const openIntercom = () => revealMessenger((intercom) => intercom("show"));

/**
 * Opens the messenger on a new conversation with `content` typed in for the
 * reader to edit and send (the help site's Contact Support on a doc page, BRE-9100).
 */
export const showIntercomNewMessage = (content: string) =>
    revealMessenger((intercom) => intercom("showNewMessage", content));

/** Longest the public help site waits for an idle moment before booting the messenger anyway. */
const IDLE_BOOT_DEADLINE_MS = 2000;
// The app id `bootIntercomWhenIdle` is waiting to boot, so an earlier click can boot it instead.
let idleBootAppId: string | null = null;

/**
 * BRE-8856: boots the messenger anonymously once the page is idle (the public
 * help site), so its script never competes with a doc's first paint. Ask
 * clicked sooner boots it on the spot. Returns a cancel for unmount.
 */
export function bootIntercomWhenIdle(appId: string): () => void {
    idleBootAppId = appId;
    const boot = () => {
        idleBootAppId = null;
        initializeIntercom(appId);
    };
    let cancelBoot: () => void;
    if (typeof window.requestIdleCallback === "function") {
        const handle = window.requestIdleCallback(boot, { timeout: IDLE_BOOT_DEADLINE_MS });
        cancelBoot = () => window.cancelIdleCallback(handle);
    } else {
        const handle = window.setTimeout(boot, 0);
        cancelBoot = () => window.clearTimeout(handle);
    }
    return () => {
        idleBootAppId = null;
        cancelBoot();
    };
}

function revealMessenger(command: (intercom: NonNullable<typeof window.Intercom>) => void) {
    if (typeof window === "undefined") return;
    if (!window.Intercom && idleBootAppId) {
        const appId = idleBootAppId;
        idleBootAppId = null;
        initializeIntercom(appId);
    }
    if (!window.Intercom) {
        offerEmailInstead("Intercom was never initialised on this page");
        return;
    }
    command(window.Intercom);
    if (messengerOpen || pendingShowTimer) return;
    pendingShowTimer = setTimeout(() => {
        pendingShowTimer = null;
        offerEmailInstead(
            `the messenger did not open within ${SHOW_CONFIRM_TIMEOUT_MS}ms (app_id ${window.intercomSettings?.app_id ?? "unset"})`
        );
    }, SHOW_CONFIRM_TIMEOUT_MS);
}
